import React, { useEffect, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { fromJS, List, Map } from 'immutable';
import { useDispatch, useSelector } from 'react-redux';

import TermsSlider from './TermsSlider';
import { getMinTermRate } from '../../../actions/companies';
import { asyncActions, error } from '../../../actions/general';
import {
  getRoute,
  getSession,
  loading,
  verifyActiveSession,
} from '../../../actions/sessions';
import {
  getShoppingCart,
  updateShoppingCart,
} from '../../../actions/shopping_carts';
import {
  usePricingSliderTest,
  usePricingTermsReverseSortTest,
} from '../../../hooks/useABTest';
import useModal from '../../../hooks/useModal';
import usePayNowHandoff from '../../../hooks/usePayNowHandoff';
import usePostHogFlag from '../../../hooks/usePostHogFlag';
import usePostHogVariant from '../../../hooks/usePostHogVariant';
import usePromo from '../../../hooks/usePromo';
import useRouteStatus from '../../../hooks/useRouteStatus';
import useSessionEntity from '../../../hooks/useSessionEntity';
import { captureCheckoutEvent } from '../../../lib/utils/checkout_events';
import { approvedCreditLineAmount } from '../../../lib/utils/credit_line';
import {
  daysAsMonths,
  formatCurrency,
  formatPercent,
} from '../../../lib/utils/formatters';
import { emitEvent } from '../../../lib/utils/modal';
import {
  PLUS_UPSELL_FEATURE_FLAG,
  rewardRateForOrder,
} from '../../../lib/utils/plus';
import {
  getNetTermDays,
  isNetTermsBucketTerm,
  isNetXCompany,
  isPayNowTerm,
  isPayOverTimeBucketTerm,
  shouldUseSectionCardLayout,
} from '../../../lib/utils/terms';
import ShoppingCartRecord from '../../../records/shopping_cart';
import ApprovalBanner from '../../shared/ApprovalBanner';
import PrimaryButton from '../../shared/buttons/PrimaryButton';
import Error from '../../shared/Error';
import Loading from '../../shared/Loading';
import LargePaymentModal from '../../shared/modal/LargePaymentModal';
import NetTermsCard from '../../shared/NetTermsCard';
import PayIn4Card from '../../shared/PayIn4Card';
import PayNowCard from '../../shared/PayNowCard';
import PayOverTimeCard from '../../shared/PayOverTimeCard';
import PlusUpsellCard from '../../shared/PlusUpsellCard';
import ProgressBars from '../../shared/ProgressBars';
import Term from '../../shared/Term';
import Tooltip from '../../shared/Tooltip';

export default function Terms({ location }) {
  const dispatch = useDispatch();
  const isMounted = useRef(true);
  const chooseHowToPayRef = useRef(null);
  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);
  const [isRouting, setIsRouting] = useState(false);
  const { isModalActive, toggleModal } = useModal();
  const [selectedTerm, setSelectedTerm] = useState({
    term: null,
    term_period: null,
    payment: null,
  });
  const [largeSinglePayment, setLargeSinglePayment] = useState(false);
  const [minRate, setMinRate] = useState();
  const hasEmittedTermsEvent = useRef(false);
  const hasEmittedPayNowOffer = useRef(false);
  const [payNowSuppressed, setPayNowSuppressed] = useState(false);
  const isLoading = useSelector((state) => state.sessions.get('loading'));
  const session = useSelector((state) => state.sessions.get('session'));
  const application = useSessionEntity('application');
  const cart = useSessionEntity('shopping_cart');
  const merchant = useSessionEntity('merchant');
  const company = useSessionEntity('company');
  const user = useSessionEntity('user');
  const promo = usePromo();
  const { inTermsReverseSortCohort } = usePricingTermsReverseSortTest();
  const { inSliderTestCohort } = usePricingSliderTest();
  const plusUpsellFlagEnabled = usePostHogFlag(PLUS_UPSELL_FEATURE_FLAG);
  const payNowTerm = cart.approved_terms.find(
    (term) => term.get('available') && isPayNowTerm(term)
  );
  const capturePayNowEvent = (component, extra) =>
    captureCheckoutEvent(
      component,
      {
        application,
        company,
        merchant,
        session,
        shopping_cart: cart,
        user,
      },
      extra
    );
  const { startHandoff, submitting } = usePayNowHandoff({
    unavailableMessage:
      'Pay Now is no longer available for this order. Please choose your terms below.',
    failureMessage: "We couldn't start Pay Now. Please try again.",
    onOutcome: (outcome, { status, reasonCode }) => {
      capturePayNowEvent('termspaynowoutcome', { outcome, status, reasonCode });
      if (outcome === 'rejected' && (status === 409 || status === 422)) {
        setPayNowSuppressed(true);
        setSelectedTerm({ term: null, term_period: null, payment: null });
      }
    },
  });

  const defaultTerm =
    company?.recharge_settings && merchant
      ? company.recharge_settings
          .find((s) => {
            return s.get('merchant_id').toString() === merchant.id.toString();
          })
          ?.get('default_term')
      : undefined;

  // Helper function to determine if we should auto-skip the terms page
  const shouldAutoSkipTerms = (cart, defaultTerm, company) => {
    if (!defaultTerm || !cart?.approved_terms) return false;

    // NETX companies always show the multi-decision card UI regardless of term count.
    if (isNetXCompany(company)) return false;

    const availableTermsCount = cart.approved_terms
      .filter((t) => t.get('available') && !isPayNowTerm(t))
      .count();

    return availableTermsCount === 1;
  };

  const setInitialTerm = (cart) => {
    if (!company || !cart.approved_terms || selectedTerm.term)
      return Promise.reject('Invalid state');

    if (cart.approved_terms.count() > 0 && !hasEmittedTermsEvent.current) {
      hasEmittedTermsEvent.current = true;
      emitEvent(
        user,
        {
          path: location.pathname,
          approved_terms: cart.approved_terms,
          credit_line: company?.credit_line,
        },
        cart
      );
    }

    const shouldAutoSkip = shouldAutoSkipTerms(cart, defaultTerm, company);

    // New section-card UI: let the user choose; no pre-selection.
    if (shouldUseSectionCardLayout(cart, company) && !shouldAutoSkip)
      return Promise.resolve();

    const net30Term = cart.approved_terms.find(
      (t) => t.get('term_period') === 'days'
    );
    const termToUse = cart.num_payments || net30Term || defaultTerm;

    const cartApprovedTerms = inTermsReverseSortCohort
      ? cart.approved_terms.reverse()
      : cart.approved_terms;
    const firstAvailable =
      net30Term ||
      cartApprovedTerms.find((t) => {
        return termToUse
          ? t.get('term') === termToUse && t.get('available')
          : t.get('available');
      });

    if (Map.isMap(firstAvailable)) {
      setSelectedTerm({
        term: firstAvailable.get('term'),
        payment: firstAvailable.get('monthly_payment'),
        term_period: firstAvailable.get('term_period'),
      });

      // Only auto-skip if company has default term AND only one available term
      return Promise.resolve(shouldAutoSkip && setLargeSinglePayment(true));
    } else {
      return Promise.reject('No valid terms');
    }
  };

  useRouteStatus(location);

  useEffect(() => {
    dispatch(asyncActions(getSession()));
  }, []);

  const save = (e) => {
    e.preventDefault();
    if (selectedTerm.term_period === 'now') {
      capturePayNowEvent('termspaynowcontinue');
      return startHandoff();
    }
    if (
      selectedTerm.term === 1 &&
      parseFloat(cart.grand_total) > parseFloat('2000.00') &&
      !largeSinglePayment
    ) {
      toggleModal();
    } else {
      setLargeSinglePayment(true);
    }
  };

  useEffect(() => {
    if (company && !minRate) {
      dispatch(loading(true));
      dispatch(asyncActions(getMinTermRate(company.id)))
        .then((res) => {
          if (res) setMinRate(res.attributes.data);
        })
        .then(() => {
          // get approved terms for states:
          //
          // 1) on no application;
          //    this means we're a returning borrower placing an order
          //
          // 2) on cart having no terms;
          //    this means a credit refresh just occured
          if (!application || cart.approved_terms.count() === 0) {
            dispatch(getShoppingCart(cart.id));
          }
        })
        .then(() => dispatch(loading(false)))
        .catch((err) => dispatch(error(err)));
    }
  }, [company]);

  useEffect(() => {
    // Set initial selected term. It can be any term row if some are
    // unavailable or a previsouly selected one.
    dispatch(loading(true));

    setInitialTerm(cart)
      .then(() => dispatch(loading(false)))
      .catch(() => dispatch(loading(false)));
  }, [cart, company]);

  useEffect(() => {
    if (largeSinglePayment && selectedTerm.term && selectedTerm.term !== 0) {
      setIsRouting(true);
      dispatch(loading(true));
      dispatch(getShoppingCart(cart?.id)).then((res) => {
        dispatch(
          asyncActions(
            verifyActiveSession(),
            updateShoppingCart(
              new ShoppingCartRecord(
                fromJS({
                  ...res.payload.data.attributes,
                  num_payments: selectedTerm.term,
                  payment_amount: selectedTerm.payment,
                  term_period: selectedTerm.term_period,
                })
              )
            ),
            getRoute()
          )
        ).then(() => {
          dispatch(loading(false));
          if (isMounted.current) setIsRouting(false);
        });
      });
    }
  }, [largeSinglePayment]);

  // Raw (un-mapped) term lists for the new NetTermsCard / PayOverTimeCard components.
  const rawNetTerms = cart.approved_terms
    .filter((i) => i.get('available') && isNetTermsBucketTerm(i))
    .sortBy((t) => getNetTermDays(t));

  const rawAvailableMonthlyTerms = inTermsReverseSortCohort
    ? cart.approved_terms
        .filter((i) => i.get('available') && isPayOverTimeBucketTerm(i))
        .sortBy((t) => t.get('term'))
        .reverse()
    : cart.approved_terms
        .filter((i) => i.get('available') && isPayOverTimeBucketTerm(i))
        .sortBy((t) => t.get('term'));

  const rawUnavailableMonthlyTerms = cart.approved_terms
    .filter((i) => !i.get('available') && isPayOverTimeBucketTerm(i))
    .sortBy((t) => t.get('term'));

  const rawBiweeklyTerms = cart.approved_terms
    .filter((i) => i.get('available') && i.get('term_period') === 'biweekly')
    .sortBy((t) => t.get('term'));

  const pi4Term = rawBiweeklyTerms.map((d, idx) => (
    <Term
      key={idx}
      term={d}
      setParentTerm={setSelectedTerm}
      parentSelectedTerm={selectedTerm}
      cart={cart}
      promo={promo}
    />
  ));

  const availableTerms = inTermsReverseSortCohort
    ? cart.approved_terms
        .filter((i) => i.get('available') && i.get('term_period') === 'monthly')
        .sortBy((t) => t.get('term'))
        .reverse()
        .map((d, idx) => (
          <Term
            key={idx}
            term={d}
            setParentTerm={setSelectedTerm}
            parentSelectedTerm={selectedTerm}
            cart={cart}
            promo={promo}
          />
        ))
    : cart.approved_terms
        .filter((i) => i.get('available') && i.get('term_period') === 'monthly')
        .sortBy((t) => t.get('term'))
        .map((d, idx) => (
          <Term
            key={idx}
            term={d}
            setParentTerm={setSelectedTerm}
            parentSelectedTerm={selectedTerm}
            cart={cart}
            promo={promo}
          />
        ));

  const unavailableTerms = cart.approved_terms
    .filter((i) => !i.get('available') && i.get('term_period') === 'monthly')
    .sortBy((t) => t.get('term'))
    .map((d, idx) => (
      <Term
        key={idx}
        term={d}
        setParentTerm={setSelectedTerm}
        parentSelectedTerm={selectedTerm}
        cart={cart}
        promo={promo}
      />
    ));

  // Get available monthly terms
  const getAvailableMonthlyTerms = () => {
    if (!cart || !cart.approved_terms) return List();
    return cart.approved_terms.filter(
      (term) => term.get('available') && term.get('term_period') === 'monthly'
    );
  };

  // Check if all available monthly terms have 0 interest
  const allTermsHaveZeroInterest = () => {
    const monthlyTerms = getAvailableMonthlyTerms();
    return (
      monthlyTerms.size > 0 &&
      monthlyTerms.every((term) => parseFloat(term.get('total_interest')) === 0)
    );
  };

  // Get the maximum term from available monthly terms
  const getMaxTerm = () => {
    const monthlyTerms = getAvailableMonthlyTerms();
    if (monthlyTerms.size === 0) return 0;
    return monthlyTerms.maxBy((term) => term.get('term'))?.get('term');
  };

  const approvalStatus = () =>
    cart?.status === 'approved' &&
    (!session.hasApplication() ||
      (session.hasApplication() && application?.status === 'approved')) ? (
      <div className="my-4 flex items-center">
        <span className="fa-layers fa-fw text-5xl">
          <FontAwesomeIcon
            icon="square"
            className="text-background-success !mx-0"
          />
          <FontAwesomeIcon
            icon={['far', 'circle-check']}
            className="text-success text-3xl !mx-1.5"
          />
        </span>
        <div className="text-xl sm:text-4xl font-extrabold">Approved</div>
      </div>
    ) : null;

  const headerSection = () => {
    if (!cart)
      return <Loading className="w-14 h-14" containerClass="my-24 sm:my-0" />;

    if (cart.using_virtual_card) {
      return (
        <>
          <div className="mt-4 mb-1 sm:mb-4">
            <div className="text-xl sm:text-4xl font-extrabold">
              Select Terms
            </div>
          </div>
          <div className="tracking-wide text-sm sm:text-lg mb-6">
            Choose your terms for your virtual card purchase and lock in 0%
            interest for {promo.get('max_net_duration')} days!
          </div>
        </>
      );
    } else {
      const maxTerm = getMaxTerm();
      const showZeroInterestPromo = allTermsHaveZeroInterest() && maxTerm > 0;

      return (
        <div className="px-4 xxs:px-0">
          {approvalStatus()}

          {pi4Term?.size > 0 && (
            <div className="sm:text-xl my-1 sm:my-3 font-semibold">
              How do you want to pay for your {formatCurrency(cart.grand_total)}{' '}
              purchase?
            </div>
          )}

          {!merchant.supports_pi4 && availableTerms?.size > 0 && (
            <>
              {promo.get('max_net_payment' > 30) ? (
                <div className="sm:text-2xl my-1 sm:my-3 font-semibold">
                  No payments for {daysAsMonths(promo.get('max_net_payment'))}
                </div>
              ) : null}
              {!isNetXCompany(company) && (
                <div className="sm:text-2xl my-1 sm:my-3 font-semibold">
                  {showZeroInterestPromo ? (
                    <>
                      0% for up to {maxTerm}{' '}
                      {maxTerm === 1 ? 'month' : 'months'}
                    </>
                  ) : (
                    <>
                      0% for {daysAsMonths(promo.get('max_net_duration'))}, up
                      to {formatCurrency(company.credit_line.get('amount'))}
                    </>
                  )}
                </div>
              )}

              {!isNetXCompany(company) && !showZeroInterestPromo && (
                <div className="text-xs xs:text-sm mobile:text-base">
                  <div className="font-light text-sm sm:text-lg tracking-wide mb-4">
                    <div>
                      If you choose to go beyond{' '}
                      {daysAsMonths(promo.get('max_net_duration'))}, you'll pay
                      the amount of interest specified below.
                    </div>
                  </div>
                  {cart.processing_fee > 0.0 && (
                    <div className="my-2">
                      <Tooltip
                        tooltipText={`For all Credit Key Anywhere orders there is a ${formatPercent(
                          cart?.processing_fee * 100
                        )} service fee. Because your merchant did not elect to cover the fee, it is added on to your order amount.`}
                        triggerClass="px-1 mt-1 hover:cursor-pointer hover:text-secondary-gray"
                        containerClass="p-3 bg-gray-100 w-3/4 sm:w-1/4 tracking-wide"
                      >
                        This order includes a{' '}
                        {formatPercent(cart?.processing_fee * 100)} Service Fee
                        <FontAwesomeIcon
                          icon={['fal', 'info-circle']}
                          className="ml-1 mt-0.5"
                        />
                      </Tooltip>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      );
    }
  };

  // Only auto-skip (show loading) if company has default term AND only one available term
  const shouldAutoSkip = shouldAutoSkipTerms(cart, defaultTerm, company);
  const useSectionCardLayout = shouldUseSectionCardLayout(cart, company);
  // Announce an approval from this session's application. Returning borrowers
  // without one have nothing new to announce. ck-web sets previous_tcl only
  // alongside has_credit_changes, so any value (even "0.00") means
  // HasCreditChanges already explained the line; an unchanged refresh skips
  // that screen, so the banner is its announcement.
  const showApprovalBanner =
    cart?.status === 'approved' &&
    session.hasApplication() &&
    application?.status === 'approved' &&
    !application.previous_tcl;
  const termBadgeVariant = usePostHogVariant(
    useSectionCardLayout && !shouldAutoSkip
      ? 'displaying-term-percentage-rates-on-checkout'
      : null
  );
  const showRateBadge = termBadgeVariant !== 'test';
  const plusRewardRate = rewardRateForOrder(session, cart, merchant);
  const showPayOverTimeCard =
    rawAvailableMonthlyTerms?.size > 0 ||
    (isNetXCompany(company) && rawUnavailableMonthlyTerms?.size > 0);
  const showPayNowCard = !!(
    payNowTerm &&
    !payNowSuppressed &&
    company &&
    merchant &&
    cart &&
    promo &&
    useSectionCardLayout &&
    !shouldAutoSkip
  );

  useEffect(() => {
    if (showPayNowCard && !hasEmittedPayNowOffer.current) {
      hasEmittedPayNowOffer.current = true;
      capturePayNowEvent('termspaynowoffered');
    }
  }, [showPayNowCard]);

  if (!company || !merchant || !cart || !promo || shouldAutoSkip) {
    return <Loading className="w-14 h-14" containerClass="my-24 sm:my-16" />;
  }

  // If in slider test cohort, render TermsSlider component instead
  if (inSliderTestCohort && !useSectionCardLayout) {
    return <TermsSlider location={location} />;
  }

  if (useSectionCardLayout) {
    return (
      <div className="px-4 sm:px-6 pb-16">
        <ProgressBars />
        <Error />
        {showApprovalBanner && (
          <ApprovalBanner
            amount={approvedCreditLineAmount(company)}
            dismissKey={application.id}
            onDismiss={() => chooseHowToPayRef.current?.focus()}
          />
        )}
        <div className="flex flex-col md:flex-row md:gap-12 mt-4">
          {/* Right rail (DOM-first so mobile stacks it above the cards):
              order total with the Plus upsell directly beneath it */}
          <div className="flex flex-col md:w-[340px] md:shrink-0 md:order-2 mb-4 md:mb-0">
            <div className="md:sticky md:top-20">
              <div className="rounded-lg border border-primary-border-gray bg-gray-100 shadow-sm p-5">
                <p className="text-xs uppercase tracking-widest text-secondary-gray mb-1">
                  Order Total
                </p>
                <p className="text-3xl font-bold text-primary">
                  {formatCurrency(cart.grand_total)}
                </p>
                <p className="text-sm font-semibold text-primary mt-1">
                  {merchant.name}
                </p>
              </div>
              {/* Bank account on file only: guarantees the trial's day-30
                  subscription charge is billable */}
              {!isNetXCompany(company) &&
                plusUpsellFlagEnabled &&
                company?.is_bank_connected &&
                plusRewardRate !== null && (
                  <PlusUpsellCard
                    cart={cart}
                    company={company}
                    rewardRate={plusRewardRate}
                  />
                )}
            </div>
          </div>

          {/* Left panel */}
          <div className="flex-1 min-w-0 md:max-w-[580px] md:order-1">
            <h2
              ref={chooseHowToPayRef}
              tabIndex={-1}
              className="text-xl font-bold text-primary mb-4"
            >
              Choose how to pay
            </h2>
            {!isNetXCompany(company) && rawBiweeklyTerms?.size > 0 && (
              <PayIn4Card
                showRateBadge={showRateBadge}
                term={rawBiweeklyTerms.first()}
                selectedTerm={selectedTerm}
                setSelectedTerm={setSelectedTerm}
                onContinue={save}
                cart={cart}
                isLoading={isLoading || isRouting || submitting}
              />
            )}
            {rawNetTerms?.size > 0 && (
              <NetTermsCard
                showRateBadge={showRateBadge}
                terms={rawNetTerms}
                selectedTerm={selectedTerm}
                setSelectedTerm={setSelectedTerm}
                onContinue={save}
                cart={cart}
                isLoading={isLoading || isRouting || submitting}
              />
            )}
            {showPayOverTimeCard && (
              <PayOverTimeCard
                showRateBadge={showRateBadge}
                terms={rawAvailableMonthlyTerms}
                unavailableTerms={rawUnavailableMonthlyTerms}
                selectedTerm={selectedTerm}
                setSelectedTerm={setSelectedTerm}
                onContinue={save}
                cart={cart}
                promo={promo}
                isLoading={isLoading || isRouting || submitting}
              />
            )}
            {/* Pay Now renders last so financing options lead (sc-27648). */}
            {showPayNowCard && (
              <PayNowCard
                showRateBadge={showRateBadge}
                term={payNowTerm}
                selectedTerm={selectedTerm}
                setSelectedTerm={(term) => {
                  setSelectedTerm(term);
                  capturePayNowEvent('termspaynowselected');
                }}
                onContinue={save}
                cart={cart}
                isLoading={isLoading || isRouting || submitting}
              />
            )}
          </div>
        </div>

        <LargePaymentModal
          cart={cart}
          isModalActive={isModalActive}
          merchant={merchant}
          setLargeSinglePayment={setLargeSinglePayment}
          toggleModal={toggleModal}
        />
      </div>
    );
  }

  return (
    <div
      id="content"
      role="main"
      className="mx-6 sm:mx-auto sm:px-4 md:px-6 md:mb-6"
    >
      <ProgressBars />
      <div>
        <Error />
        {headerSection()}
      </div>

      <div className="mx-auto">
        {pi4Term}
        {pi4Term?.size > 0 &&
          (unavailableTerms?.size > 0 || availableTerms?.size > 0) && (
            <>
              <div className="text-lg text-center w-full border-b-[2.5px] mt-6 h-[17px]">
                <span className="sm:text-lg font-semibold bg-white p-6">
                  Or pay over time
                </span>
              </div>
              <div className="text-center text-secondary-gray text-sm sm:text-base p-3 sm:p-6">
                No early pay-off fees / 0% for 30 days
              </div>
            </>
          )}
        {unavailableTerms}
        {availableTerms}
      </div>

      <LargePaymentModal
        cart={cart}
        isModalActive={isModalActive}
        merchant={merchant}
        setLargeSinglePayment={setLargeSinglePayment}
        toggleModal={toggleModal}
      />

      {cart?.approved_terms.count() !== 0 &&
        (!selectedTerm.term || selectedTerm.term === 0) && (
          <div className="text-xs sm:text-sm text-error my-2">
            Please select a term above.
          </div>
        )}

      <PrimaryButton
        buttonText="Continue"
        buttonColor="bg-primary-blue"
        hoverClass="hover:bg-primary-blue-hover"
        disabled={isLoading || isRouting || submitting}
        isLoading={isLoading || isRouting || submitting}
        onClick={save}
      />
    </div>
  );
}
